import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import {
	getUserOrder,
	updateOrderStatus,
} from '../../redux/actions/orderActions';
import { AddItemToCart, updateCartItemQuantity } from '../../redux/actions/cartActions';
import Navbar from '../../component/navbar';
import Footer from '../../component/footer';
import {
	Box,
	Heading,
	Text,
	Image,
	Spinner,
	Center,
	Flex,
	Container,
	Divider,
	Stack,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Badge,
	Button,
	Card,
	CardBody,
	SimpleGrid,
	HStack,
	Icon,
} from '@chakra-ui/react';
import { MdShoppingBag, MdCalendarToday, MdLocalShipping, MdEmail, MdRefresh, MdDownload } from 'react-icons/md';
import Alert from '../../component/alert';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const statusColor = (status) => {
	switch (status?.toLowerCase()) {
		case 'completed': return 'green';
		case 'processing': return 'yellow';
		case 'shipped': return 'cyan';
		case 'pending': return 'blue';
		case 'canceled': return 'red';
		default: return 'gray';
	}
};

const InfoBlock = ({ icon, label, value }) => (
	<Box>
		<HStack spacing={2} mb={1}>
			<Icon as={icon} boxSize={4} color="gray.400" />
			<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">{label}</Text>
		</HStack>
		<Text fontWeight="semibold" color="gray.800" fontSize="sm">{value || '—'}</Text>
	</Box>
);

const OrderDetail = ({ getUserOrder, loading, orders, updateOrderStatus, AddItemToCart, updateCartItemQuantity }) => {
	const { order_id } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		getUserOrder(order_id);
	}, [getUserOrder, order_id]);

	const handleCancelOrder = () => {
		if (window.confirm('Are you sure you want to cancel this order?')) {
			updateOrderStatus(order_id, 'Canceled');
		}
	};

	const handleEditOrder = () => {
		navigate(`/order/edit/${order_id}`);
	};

	const handleReorder = async () => {
		let added = 0;
		for (const item of orders) {
			const ok = await AddItemToCart({
				product_id: item.product_id,
				quantity: item.quantity,
				variant_id: item.variant_id || null,
			});
			if (ok) added += 1;
		}
		if (added > 0) {
			navigate('/cart');
		}
	};

	const orderDetails = orders && orders.length > 0 ? orders[0] : null;
	const total = orders?.reduce((acc, item) => {
		return acc + parseFloat(item.price || 0) * parseFloat(item.quantity || 1);
	}, 0) || 0;

	const handleDownloadInvoice = () => {
		if (!orderDetails) return;
		const doc = new jsPDF();
		const pageWidth = doc.internal.pageSize.getWidth();

		// Header
		doc.setFontSize(20);
		doc.setFont('helvetica', 'bold');
		doc.text('ORDER SUMMARY', pageWidth / 2, 20, { align: 'center' });
		doc.setFontSize(10);
		doc.setFont('helvetica', 'normal');
		doc.setTextColor(100);
		doc.text('CorrectCom Sales', pageWidth / 2, 28, { align: 'center' });
		doc.setDrawColor(200);
		doc.line(14, 33, pageWidth - 14, 33);

		// Order info columns
		doc.setTextColor(0);
		doc.setFontSize(10);
		const dateStr = new Date(orderDetails.created_at).toLocaleDateString('en-US', {
			year: 'numeric', month: 'long', day: 'numeric',
		});
		const infoLeft = [
			['Order #:', `#${orderDetails.order_id}`],
			['Date:', dateStr],
			['Status:', orderDetails.status || 'Pending'],
		];
		const infoRight = [
			['Customer:', orderDetails.email || '—'],
			['Ship To:', orderDetails.shipping_address || '—'],
			...(orderDetails.po_number ? [['PO Number:', orderDetails.po_number]] : []),
		];

		let yLeft = 40;
		infoLeft.forEach(([label, value]) => {
			doc.setFont('helvetica', 'bold');
			doc.text(label, 14, yLeft);
			doc.setFont('helvetica', 'normal');
			doc.text(String(value), 55, yLeft);
			yLeft += 7;
		});

		let yRight = 40;
		infoRight.forEach(([label, value]) => {
			doc.setFont('helvetica', 'bold');
			doc.text(label, pageWidth / 2 + 5, yRight);
			doc.setFont('helvetica', 'normal');
			const wrapped = doc.splitTextToSize(String(value), 65);
			doc.text(wrapped, pageWidth / 2 + 35, yRight);
			yRight += 7 * wrapped.length;
		});

		// Notes
		let notesEndY = Math.max(yLeft, yRight) + 4;
		if (orderDetails.notes) {
			doc.setFont('helvetica', 'bold');
			doc.text('Notes:', 14, notesEndY);
			doc.setFont('helvetica', 'normal');
			doc.setTextColor(80);
			const noteLines = doc.splitTextToSize(orderDetails.notes, pageWidth - 28);
			doc.text(noteLines, 14, notesEndY + 6);
			doc.setTextColor(0);
			notesEndY += 6 + noteLines.length * 5;
		}

		// Items table
		const tableBody = orders.map((item) => [
			item.product_name,
			String(item.quantity),
			`$${parseFloat(item.price).toFixed(2)}`,
			`$${(parseFloat(item.price) * parseFloat(item.quantity || 1)).toFixed(2)}`,
		]);

		autoTable(doc, {
			startY: notesEndY + 6,
			head: [['Product', 'Qty', 'Unit Price', 'Subtotal']],
			body: tableBody,
			headStyles: { fillColor: [66, 153, 225], textColor: 255, fontSize: 9 },
			bodyStyles: { fontSize: 9 },
			columnStyles: {
				1: { halign: 'right', cellWidth: 20 },
				2: { halign: 'right', cellWidth: 30 },
				3: { halign: 'right', cellWidth: 30 },
			},
			styles: { lineColor: [220, 220, 220], lineWidth: 0.1 },
		});

		const finalY = (doc as any).lastAutoTable.finalY + 8;
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(11);
		doc.text(`Order Total: $${total.toFixed(2)}`, pageWidth - 14, finalY, { align: 'right' });

		doc.save(`order-summary-${orderDetails.order_id}.pdf`);
	};

	return (
		<Flex direction="column" minH="100vh" bg="gray.50">
			<Navbar />
			<Alert />
			<Box flex="1">
				<Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
					{loading ? (
						<Center minH="60vh">
							<Spinner size="xl" thickness="4px" color="blue.500" />
						</Center>
					) : orderDetails ? (
						<>
							{/* Header */}
							<HStack mb={6} spacing={3}>
								<Flex w="40px" h="40px" borderRadius="lg" bg="blue.50" align="center" justify="center">
									<Icon as={MdShoppingBag} boxSize={5} color="blue.500" />
								</Flex>
								<Box>
									<Heading size="lg" color="gray.800">Order Details</Heading>
									<Text fontSize="sm" color="gray.500">Order #{orderDetails.order_id}</Text>
								</Box>
							</HStack>

							{/* Summary Card */}
							<Card borderRadius="2xl" shadow="sm" mb={5}>
								<CardBody>
									<SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={6} mb={6}>
										<InfoBlock
											icon={MdCalendarToday}
											label="Date Placed"
											value={new Date(orderDetails.created_at).toLocaleDateString('en-US', {
												year: 'numeric', month: 'long', day: 'numeric',
											})}
										/>
										<InfoBlock icon={MdEmail} label="Ordered By" value={orderDetails.email} />
										<InfoBlock icon={MdLocalShipping} label="Ship To" value={orderDetails.shipping_address} />
										<Box>
											<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={1}>Status</Text>
											<Badge
												colorScheme={statusColor(orderDetails.status)}
												variant="subtle"
												borderRadius="full"
												px={3}
												py={1}
												fontSize="sm"
											>
												{orderDetails.status || 'Pending'}
											</Badge>
										</Box>
									</SimpleGrid>

									{/* PO Number + Notes */}
									{(orderDetails.po_number || orderDetails.notes) && (
										<>
											<Divider mb={4} />
											<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
												{orderDetails.po_number && (
													<Box>
														<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={1}>PO Number</Text>
														<Text fontWeight="semibold" color="gray.800">{orderDetails.po_number}</Text>
													</Box>
												)}
												{orderDetails.notes && (
													<Box>
														<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={1}>Order Notes</Text>
														<Text color="gray.700" fontSize="sm">{orderDetails.notes}</Text>
													</Box>
												)}
											</SimpleGrid>
										</>
									)}

									<Divider mb={4} />

									<Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
										<Box>
											<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">Order Total</Text>
											<Text fontSize="2xl" fontWeight="bold" color="teal.600">${total.toFixed(2)}</Text>
										</Box>
										<Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
											<Button
												leftIcon={<Icon as={MdDownload} />}
												colorScheme="blue"
												variant="outline"
												borderRadius="xl"
												onClick={handleDownloadInvoice}
											>
												Download Order Summary
											</Button>
											<Button
												leftIcon={<Icon as={MdRefresh} />}
												colorScheme="teal"
												variant="outline"
												borderRadius="xl"
												onClick={handleReorder}
											>
												Reorder
											</Button>
											{orderDetails.status?.toLowerCase() === 'pending' && (
												<>
													<Button colorScheme="blue" borderRadius="xl" onClick={handleEditOrder}>
														Edit Order
													</Button>
													<Button colorScheme="red" variant="outline" borderRadius="xl" onClick={handleCancelOrder}>
														Cancel Order
													</Button>
												</>
											)}
										</Stack>
									</Flex>
								</CardBody>
							</Card>

							{/* Items Card */}
							<Card borderRadius="2xl" shadow="sm" overflow="hidden">
								<CardBody pb={0}>
									<Heading size="sm" color="gray.700" mb={4}>Items Ordered</Heading>
								</CardBody>
								<Box overflowX="auto">
									<Table variant="simple">
										<Thead>
											<Tr bg="gray.50">
												<Th fontSize="xs" color="gray.500">Product</Th>
												<Th fontSize="xs" color="gray.500">ID</Th>
												<Th fontSize="xs" color="gray.500" isNumeric>Qty</Th>
												<Th fontSize="xs" color="gray.500" isNumeric>Unit Price</Th>
												<Th fontSize="xs" color="gray.500" isNumeric>Subtotal</Th>
											</Tr>
										</Thead>
										<Tbody>
											{orders.map((item) => (
												<Tr key={item._id || item.product_id}>
													<Td>
														<Flex align="center" gap={3}>
															<Image
																src={`${BASE_URL}/api/image?filename=${item.image}`}
																boxSize="48px"
																objectFit="contain"
																borderRadius="lg"
																bg="white"
																border="1px solid"
																borderColor="gray.100"
															/>
															<Box>
																<Text fontWeight="semibold" fontSize="sm" color="gray.800">{item.product_name}</Text>
																{item.variant_label && (
																	<Badge colorScheme="purple" fontSize="xs" mt={0.5}>{item.variant_label}</Badge>
																)}
																{item.product_description && (
																	<Text fontSize="xs" color="gray.400" noOfLines={1}>{item.product_description}</Text>
																)}
															</Box>
														</Flex>
													</Td>
													<Td><Text fontSize="xs" color="gray.500">#{item.product_id}</Text></Td>
													<Td isNumeric><Text fontWeight="medium">{item.quantity}</Text></Td>
													<Td isNumeric><Text color="gray.600">${parseFloat(item.price).toFixed(2)}</Text></Td>
													<Td isNumeric>
														<Text fontWeight="semibold" color="gray.800">
															${(parseFloat(item.price) * parseFloat(item.quantity || 1)).toFixed(2)}
														</Text>
													</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								</Box>
								<Box px={6} py={4} bg="gray.50" borderTop="1px solid" borderColor="gray.100">
									<Flex justify="flex-end">
										<HStack spacing={4}>
											<Text color="gray.500" fontSize="sm">Total</Text>
											<Text fontWeight="bold" fontSize="lg" color="teal.600">${total.toFixed(2)}</Text>
										</HStack>
									</Flex>
								</Box>
							</Card>

							<Button variant="ghost" mt={6} onClick={() => navigate(-1)} color="gray.500" size="sm">
								← Back to Orders
							</Button>
						</>
					) : (
						<Center minH="60vh">
							<Box textAlign="center">
								<Flex w="64px" h="64px" borderRadius="2xl" bg="gray.100" align="center" justify="center" mx="auto" mb={4}>
									<Icon as={MdShoppingBag} boxSize={8} color="gray.400" />
								</Flex>
								<Heading size="md" color="gray.600">Order Not Found</Heading>
								<Text color="gray.400" mt={2}>This order doesn't exist or you don't have permission to view it.</Text>
								<Button mt={4} colorScheme="blue" borderRadius="xl" onClick={() => navigate('/order')}>
									Back to Orders
								</Button>
							</Box>
						</Center>
					)}
				</Container>
			</Box>
			<Footer />
		</Flex>
	);
};

const mapStateToProps = (state) => ({
	orders: state.order.userorder,
	loading: state.order.userloading,
});

export default connect(mapStateToProps, { getUserOrder, updateOrderStatus, AddItemToCart, updateCartItemQuantity })(
	OrderDetail
);
